/**
 * Runs a callback on the user's first interaction (mousemove, click, scroll or touch), instead
 * of on page idle - defers third-party script injection until the user is actually engaged.
 * Falls back to firing after `fallbackTimeout` ms even without interaction, so the script still
 * loads for visitors who never move/scroll/touch (e.g. reading a short page without any input).
 * @param {function} callback function to call on first interaction or fallback timeout
 * @param {number} fallbackTimeout ms to wait before firing anyway if no interaction happens (default 20s)
 */
export const onFirstInteraction = (callback, fallbackTimeout = 20000) => {
  const events = ['mousemove', 'click', 'scroll', 'touchstart'];
  let fired = false;

  const fire = () => {
    if (fired) {
      return;
    }
    fired = true;
    clearTimeout(fallbackTimer);
    events.forEach(event => window.removeEventListener(event, fire));
    callback();
  };

  const fallbackTimer = setTimeout(fire, fallbackTimeout);
  events.forEach(event => window.addEventListener(event, fire, { once: true, passive: true }));
};

/**
 * Runs a callback once the browser is idle (or on window load as a fallback), same behaviour
 * third-party script injection used before it was deferred to first interaction.
 * @param {function} callback function to call once the page is idle
 */
export const onIdle = callback => {
  const schedule = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 5000 });
    } else {
      window.setTimeout(callback, 200);
    }
  };
  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
};

/**
 * Whether Usercentrics already has a stored consent decision for this browser
 * ('ucData'/'ucString' in localStorage) - returning visitors with a known consent state don't
 * need to wait for interaction, so third-party scripts can load on idle as before.
 * @returns {boolean}
 */
export const hasStoredConsentDecision = () => {
  try {
    return Boolean(window.localStorage?.getItem('ucData')) && Boolean(window.localStorage?.getItem('ucString'));
  } catch {
    return false;
  }
};

/**
 * Loads third-party scripts on idle for returning visitors with a known consent decision,
 * otherwise defers loading until the user's first interaction.
 * @param {function} callback function to call once ready to load
 * @param {number} fallbackTimeout ms to wait before firing anyway if no interaction happens (default 20s)
 */
export const onIdleOrFirstInteraction = (callback, fallbackTimeout = 20000) => {
  if (hasStoredConsentDecision()) {
    onIdle(callback);
  } else {
    onFirstInteraction(callback, fallbackTimeout);
  }
};

export const resizeTable = table => {
  table.querySelectorAll('tbody').forEach((tbody, index, arr) => {
    let tbodyHeight = index === 0 && arr.length > 1 ? tbody.offsetHeight + arr[index + 1].offsetHeight + 16 + 'px' : tbody.offsetHeight + 16 + 'px';
    tbody.style.setProperty('--table-height-section', tbodyHeight);
  });
};
/**
 * returns a throttled version of a functionm limiting the number of times it can be called per second
 * @param {function} eventHandler function to be throttles
 * @param {number} time time to wait between function calls in ms
 * @param {boolean} immediate should the function be called immediatly (more responsive but less accurate)
 * @param {boolean} accurate if true makes the immediate function accurate on a small performance cost
 * @returns {function} throttled event Handler
 */
export const throttle = (eventHandler, time, immediate = false, accurate = false) => {
  let throttlePause;

  return (...args) => {
    if (throttlePause) {
      return;
    }

    throttlePause = true;

    if (immediate) {
      eventHandler(...args);
    }
    setTimeout(() => {
      if (!immediate || accurate) {
        eventHandler(...args);
      }
      throttlePause = false;
    }, time);
  };
};

/**
 * returns a debounced version of a function. Function is only called after it wasn't called again for a certain amount of time
 * @param {function} eventHandler function to be debounced
 * @param {number} time time to wait before function is called
 * @param {boolean} immediate if true function is called immediatly
 * @returns debounced event Handler
 */
export const debounce = (eventHandler, time, immediate = false) => {
  let timeout;

  return (...args) => {
    const later = () => {
      timeout = null;
      if (!immediate) {
        eventHandler(...args);
      }
    };

    const callNow = immediate && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(later, time);

    if (callNow) {
      eventHandler(...args);
    }
  };
};
